import { ConditionTemplate, Finding, FindingsByOrgan, RenderedSummary } from '@/api';

import {
  DEFAULT_QUESTION,
  FINDING_CONDITION_TEMPLATE,
  MIN_FINDING_HIGH_SCORE,
  ML_SERVER,
  POST_MENOPAUSAL_CONDITION,
  SPONDYLOARTHROPATHY_CONDITION,
  SPONDYLOARTHROPATHY_CONDITIONS,
} from '../constant';
import { OrganLink } from '../model';

export const groupFindingsByOrgan = (data: RenderedSummary[]): FindingsByOrgan => {
  const groupedData: FindingsByOrgan = {};

  data.forEach((item) => {
    if (item.reportFindings.length === 0) {
      return;
    }

    const organ = item?.reportFindings[0]?.template?.organ || '';

    if (!groupedData[organ]) {
      groupedData[organ] = [];
    }

    const renderedSummaryData: Finding = {
      conditionTemplate: {
        condition: item.reportFindings[0].template?.condition,
        conditionDetail: item.reportFindings[0].template?.conditionDetail,
        normalStatementOutcome: item.reportFindings[0].template?.normalStatementOutcome,
        organ,
        uuid: item.reportFindings[0].template?.uuid,
      },
      renderedSummary: {
        actionDetails: item.actionDetails,
        actionSummary: item.actionSummary,
        aiAcknowledged: item.aiAcknowledged,
        createdBy: item.createdBy,
        csdScore: item.csdScore,
        findingDetails: item.findingDetails,
        findingSummary: item.findingSummary,
        keyImages: item.keyImages,
        newFinding: item.newFinding,
        oncoradsScore: item.oncoradsScore,
        reportFindings: item.reportFindings,
        reportFindingsUuids: item.reportFindingsUuids,
        reportUuid: item.reportUuid,
        status: '',
        uuid: item.uuid,
      },
    };

    groupedData[organ].push(renderedSummaryData);
  });

  return groupedData;
};

export const removeDuplicateConditions = (findings: Finding[]): Finding[] => {
  if (findings) {
    const seenConditions = new Set<string>();
    const dedupedFindings: Finding[] = [];
    const otherFindings: Finding[] = [];

    findings.forEach((finding) => {
      const { condition } = finding.conditionTemplate;

      if (condition === FINDING_CONDITION_TEMPLATE.OTHER) {
        otherFindings.push(finding);
      } else if (condition && !seenConditions.has(condition)) {
        dedupedFindings.push(finding);
        seenConditions.add(condition);
      } else if (finding.renderedSummary.createdBy !== ML_SERVER) {
        const index = dedupedFindings.findIndex((f) => f.conditionTemplate.condition === condition);

        if (index !== -1) {
          dedupedFindings[index] = finding;
        }
      }
    });

    return [...dedupedFindings, ...otherFindings];
  }

  return [];
};

export const categorizeFindings = (
  findings: Finding[],
): { commonFindings: Finding[]; defaultFindings: Finding[] } => {
  const defaultFindings: Finding[] = [];
  const commonFindings: Finding[] = [];

  findings.forEach((finding) => {
    const { condition } = finding.conditionTemplate;

    if (condition?.startsWith('default_question')) {
      defaultFindings.push(finding);
    } else {
      commonFindings.push(finding);
    }
  });

  return { commonFindings, defaultFindings: removeDuplicateConditions(defaultFindings) };
};

export const getFindingsWithHighScores = (data: FindingsByOrgan): FindingsByOrgan => {
  const result: FindingsByOrgan = {};

  Object.keys(data).forEach((key) => {
    const organData = data[key];

    organData.forEach((item) => {
      const { csdScore, oncoradsScore } = item.renderedSummary;

      if (oncoradsScore > MIN_FINDING_HIGH_SCORE || csdScore > MIN_FINDING_HIGH_SCORE) {
        result[key] = [...(result[key] ?? []), item];
      }
    });
  });

  return result;
};

export const getConditionTemplates = (
  findings: FindingsByOrgan,
): {
  [key: string]: ConditionTemplate[];
} =>
  Object.entries(findings).reduce<{ [key: string]: ConditionTemplate[] }>(
    (acc, [organ, organFindings]) => {
      if (organ === 'spine') {
        acc[organ] = organFindings
          .map((finding) => finding.conditionTemplate as ConditionTemplate)
          .filter((finding) => finding.condition !== SPONDYLOARTHROPATHY_CONDITION);
      } else {
        acc[organ] = organFindings.map((finding) => finding.conditionTemplate as ConditionTemplate);
      }

      return acc;
    },
    {},
  );

export const mergeFindings = (
  links: OrganLink[],
  defaultFindings: FindingsByOrgan,
  findings: FindingsByOrgan,
): FindingsByOrgan =>
  links.reduce((acc, navData) => {
    const organKey = navData.key;
    const organFindings = findings[organKey] || [];
    const defaultOrganFindings = defaultFindings[organKey] || [];

    acc[organKey] = [...defaultOrganFindings, ...organFindings];

    if (navData.sub?.length) {
      navData.sub.forEach((subOrgan) => {
        const subOrganKey = subOrgan.key;
        const subOrganFindings = findings[subOrganKey] || [];
        const defaultSubOrganFindings = defaultFindings[subOrganKey] || [];

        acc[subOrganKey] = [...defaultSubOrganFindings, ...subOrganFindings].map((finding) => ({
          ...finding,
          parentOrgan: organKey,
        }));
      });
    }

    return acc;
  }, {} as FindingsByOrgan);

export const isFieldAiGenerated = ({
  aiFindings,
  condition,
  formValues,
  name,
  organ,
  value,
}: {
  aiFindings: FindingsByOrgan | null;
  condition: string;
  formValues: { [key: string]: boolean | null | string | string[] } | null;
  name: string;
  organ: string;
  value: string;
}): boolean => {
  if (!aiFindings?.[organ]) {
    return false;
  }

  const finding = aiFindings[organ].find(
    (selectedFinding) => selectedFinding.conditionTemplate.condition === condition,
  );

  const conditionDetail = finding?.renderedSummary.reportFindings[0].conditionDetail;
  const selectedCondition = conditionDetail?.find((conditionData) => conditionData.name === name);

  if (
    condition === DEFAULT_QUESTION.ENDOMETRIAL_THICKNESS &&
    formValues?.assess_endometrial_thickness
  ) {
    return false;
  }

  if (Array.isArray(selectedCondition?.value)) {
    const selectedValues = selectedCondition?.value.map((v) => v.toLowerCase());

    return selectedValues.includes(value.toLowerCase());
  }

  return selectedCondition?.value === value;
};

export const checkIfMIFGenerated = (name: string) => {
  const mifGenerated = [POST_MENOPAUSAL_CONDITION];

  return mifGenerated.includes(name);
};

export const getDefaultPostMenopausalValue = (status: null | string | undefined) => {
  if (status === 'postMenopausal') {
    return true;
  }

  if (status === 'menstrual' || status === 'menopausal') {
    return false;
  }

  return '';
};

export const sortAndGroupFindings = (findings: Finding[]) => {
  const spondyloarthropathyFindings: Finding[] = [];
  const otherFindings: Finding[] = [];
  let startIndex = 0;
  let indexUpdated = false;

  findings.forEach((finding, index) => {
    if (SPONDYLOARTHROPATHY_CONDITIONS.includes(finding.conditionTemplate.condition!)) {
      if (!indexUpdated) {
        startIndex = index;
        indexUpdated = true;
      }

      spondyloarthropathyFindings.push(finding);
    } else {
      otherFindings.push(finding);
    }
  });

  spondyloarthropathyFindings.sort(
    (a, b) =>
      SPONDYLOARTHROPATHY_CONDITIONS.indexOf(a.conditionTemplate.condition!) -
      SPONDYLOARTHROPATHY_CONDITIONS.indexOf(b.conditionTemplate.condition!),
  );

  if (spondyloarthropathyFindings.length > 0) {
    const updatedFindings = [
      ...otherFindings.slice(0, startIndex),
      ...spondyloarthropathyFindings,
      ...otherFindings.slice(startIndex),
    ];

    return updatedFindings;
  }

  return otherFindings;
};

/**
 * Merges default findings and common findings into a single object.
 *
 * @param defaultFindings - An object where the keys are strings and the values are arrays of `Finding` objects representing the default findings.
 * @param commonFindings - An object where the keys are strings and the values are arrays of `Finding` objects representing the common findings.
 * @returns An object where the keys are strings and the values are arrays of `Finding` objects, combining the default and common findings.
 *
 * @remarks
 * If the key is 'spine', the findings are sorted and grouped before merging.
 * If a key from `commonFindings` does not exist in `defaultFindings`, it is added to the result.
 * If a key exists in both `defaultFindings` and `commonFindings`, the findings are concatenated.
 */
export const mergeDefaultAndCommonFindings = (
  defaultFindings: FindingsByOrgan,
  commonFindings: FindingsByOrgan,
) => {
  const result = { ...defaultFindings };

  Object.entries(commonFindings).forEach(([key, findings]) => {
    let updatedFindings = [...findings];

    if (key === 'spine') {
      updatedFindings = sortAndGroupFindings(updatedFindings);
    }

    if (!result[key]) {
      result[key] = updatedFindings;
    } else {
      result[key] = [...result[key], ...updatedFindings];
    }
  });

  return result;
};

export const createNewRenderedSummaryPayload = (
  observationReportFindingsUuids: string[],
  newReportUuid: string,
  enableAcknowledge: boolean,
) => ({
  actionDetails: '',
  actionSummary: '',
  additionalRecommendations: '',
  aiAcknowledged: enableAcknowledge,
  clinicalNotes: '',
  csdScore: 0,
  findingDetails: '',
  findingSummary: '',
  keyImages: [],
  newFinding: true,
  oncoradsScore: 0,
  reportFindings: [],
  reportFindingsUuids: observationReportFindingsUuids,
  reportUuid: newReportUuid,
  status: 'draft' as const,
});
